// @ts-check

import { ThemeEvents, CustomizerRemoveConfirmedEvent } from '@theme/events';
import { CustomizerLayer } from '@theme/custom.customizer-layer';

/**
 * The shared "Are you sure?" removal confirmation. One instance lives inside each customizer
 * overlay. Any control's Remove button dispatches `customizer:confirm-remove` on the document
 * bus; this shows an in-overlay confirmation layer, and on "Yes, I'm sure" it echoes
 * `customizer:remove-confirmed` back so the owning control clears itself.
 */
class CustomizerConfirm extends CustomizerLayer {
  /** @type {{ configId: string, controlKey: string } | null} */
  #pending = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.customizerConfirmRemove, this.#handleRequest);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.customizerConfirmRemove, this.#handleRequest);
  }

  /** The least destructive choice takes focus, as the alertdialog pattern asks. */
  get initialFocus() {
    const keep = this.querySelector('.custom-customizer-confirm__keep');
    return keep instanceof HTMLElement ? keep : null;
  }

  /** The dialog's default body copy, captured before the first override. @type {string | null} */
  #defaultBody = null;

  /**
   * A control asked to confirm a removal: remember the target, apply its body copy (e.g. the photo
   * variant "This will remove your photo" overrides the default engraving copy), and reveal. Only
   * requests for this customizer's config id are handled.
   * @param {Event} event
   */
  #handleRequest = (event) => {
    const { configId, controlKey, body } = /** @type {CustomEvent} */ (event).detail;
    if (configId !== this.configId) return;
    this.#pending = { configId, controlKey };

    const bodyEl = this.querySelector('.custom-customizer-confirm__body');
    if (bodyEl instanceof HTMLElement) {
      this.#defaultBody ??= bodyEl.textContent ?? '';
      bodyEl.textContent = typeof body === 'string' && body !== '' ? body : this.#defaultBody;
    }
    this.openLayer();
  };

  /** "Yes, I'm sure": confirm the pending removal, then hide. Bound via `on:click="/confirm"`. */
  confirm = () => {
    if (this.#pending) {
      this.dispatchEvent(
        new CustomizerRemoveConfirmedEvent(this.#pending.configId, this.#pending.controlKey)
      );
    }
    this.closeLayer();
  };

  closeLayer() {
    this.#pending = null;
    super.closeLayer();
  }
}

if (!customElements.get('custom-customizer-confirm')) {
  customElements.define('custom-customizer-confirm', CustomizerConfirm);
}
